import {
  HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpResponseBase,
} from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import {
  Observable,
  of as _observableOf,
  throwError as _observableThrow,
  throwError,
} from 'rxjs';
import {
  catchError as _observableCatch,
  mergeMap as _observableMergeMap,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
export const API_BASE_URL = new InjectionToken<string>('API_BASE_URL');

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private http: HttpClient;
  private externalPaymentBaseUrl: string;

  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;
  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.http = http;
    this.externalPaymentBaseUrl = environment.externalPaymentBaseUrl;
  }

  /**
   * @return OK
   */
  getExternalPaymentReservation(
    entityId: string,
    token?: string
  ): Observable<ResponseModel> {
    let url_ = this.externalPaymentBaseUrl + '/getReservationDetails?';

    url_ += 'entityId=' + encodeURIComponent(entityId);
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'blob',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        'x-temporary-token': entityId
      }),
    };

    return this.http
      .request('get', url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processRequest(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processRequest(<any>response_);
            } catch (e) {
              return <Observable<ResponseModel>>(<any>_observableThrow(e));
            }
          } else
            return <Observable<ResponseModel>>(
              (<any>_observableThrow(response_))
            );
        })
      );
  }

 /**
   * @return OK
   */
 getTransactionUrl (
  entityId: string,
  lang: string,
  currency: string,
  token?:string
): Observable<TransactionUrlResponse> {
  let url_ = this.externalPaymentBaseUrl + '/getTransactionUrl?';

  url_ += 'entityId=' + encodeURIComponent(entityId) + '&';
  url_ += 'lang=' + encodeURIComponent(lang) + '&';
  url_ += 'currency=' + encodeURIComponent(currency) + '&';
  url_ = url_.replace(/[?&]$/, '');

  let options_: any = {
    observe: 'response',
    responseType: 'blob',
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
      'x-temporary-token': entityId
    }),
  };

  return this.http
    .request('get', url_, options_)
    .pipe(
      _observableMergeMap((response_: any) => {
        return this.processRequest(response_);
      })
    )
    .pipe(
      _observableCatch((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processRequest(<any>response_);
          } catch (e) {
            return <Observable<TransactionUrlResponse>>(<any>_observableThrow(e));
          }
        } else
          return <Observable<TransactionUrlResponse>>(
            (<any>_observableThrow(response_))
          );
      })
    );
}

  protected processRequest(response: HttpResponseBase): Observable<any> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (<any>response).error instanceof Blob
        ? (<any>response).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText) => {
          let resultData200 =
            _responseText === ''
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          return _observableOf(resultData200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText) => {
          return this.throwException(
            'An unexpected server error occurred.',
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(<any>null);
  }

  private throwException(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result?: any
  ): Observable<any> {
    const res = JSON.parse(response);

    if (result !== null && result !== undefined) return throwError(result);
    else
      return throwError(
        new ApiException(message, status, response, headers, null)
      );
  }

}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next('');
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = (event) => {
        observer.next((<any>event.target).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}

export class ApiException extends Error {
  status: number;
  response: string;
  headers: { [key: string]: any };
  result: any;

  constructor(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result: any
  ) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiException {
    return obj.isApiException === true;
  }
}

export class ResponseModel implements IResponseModel {
  success?: boolean | null;
  content?: any | null;
  message?: any | null;

  constructor(data?: IResponseModel) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.success =
        _data['success'] !== undefined ? _data['success'] : <any>null;
      this.content =
        _data['content'] !== undefined ? _data['content'] : <any>null;
      this.message =
        _data['message'] !== undefined ? _data['message'] : <any>null;
    }
  }

  static fromJS(data: any): ResponseModel {
    data = typeof data === 'object' ? data : {};
    let result = new ResponseModel();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === 'object' ? data : {};
    data['success'] = this.success !== undefined ? this.success : <any>null;
    data['content'] = this.content !== undefined ? this.content : <any>null;
    data['message'] = this.content !== undefined ? this.content : <any>null;
    return data;
  }
}

export interface IResponseModel {
  success?: boolean | null;
  content?: any | null;
  message?: any | null;
}
export class TransactionUrlResponse {
 success?: boolean | null;
 transactionUrl?: string | null;

 constructor(data?: TransactionUrlResponse) {
  if (data) {
    for (var property in data) {
      if (data.hasOwnProperty(property))
        (<any>this)[property] = (<any>data)[property];
    }
  }
}

init(_data?: any) {
  if (_data) {
    this.success =
      _data['success'] !== undefined ? _data['success'] : <any>null;
    this.transactionUrl =
      _data['transactionUrl'] !== undefined ? _data['transactionUrl'] : <any>null;
    
  }
}

static fromJS(data: any): TransactionUrlResponse {
  data = typeof data === 'object' ? data : {};
  let result = new TransactionUrlResponse();
  result.init(data);
  return result;
}

toJSON(data?: any) {
  data = typeof data === 'object' ? data : {};
  data['success'] = this.success !== undefined ? this.success : <any>null;
  data['transactionUrl'] = this.transactionUrl !== undefined ? this.transactionUrl : <any>null;
  return data;
}


}